import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

const HelpPage: React.FC = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const faqs = [
    {
      id: 1,
      question: "Cum pot accesa cărțile din bibliotecă?",
      answer: "Pentru a accesa cărțile, trebuie să te înregistrezi pe platformă și să te autentifici. După autentificare, poți naviga prin colecțiile noastre și poți citi cărțile direct în browser sau le poți descărca pentru lectură offline."
    },
    {
      id: 2,
      question: "Este biblioteca gratuită?",
      answer: "Da, majoritatea resurselor din biblioteca noastră sunt disponibile gratuit. Unele materiale speciale sau premium pot necesita un abonament, dar o gamă largă de cărți și documente sunt accesibile fără cost."
    },
    {
      id: 3,
      question: "Cum pot sugera o carte pentru a fi adăugată în colecție?",
      answer: "Poți sugera cărți prin pagina de contact sau prin secțiunea de feedback din contul tău. Echipa noastră va evalua sugestiile și va încerca să adauge resursele relevante în funcție de disponibilitate și drepturi de autor."
    },
    {
      id: 4,
      question: "Pot descărca cărțile pentru citire offline?",
      answer: "Da, majoritatea cărților pot fi descărcate în formatele PDF sau EPUB pentru citire offline. Această funcționalitate este disponibilă pentru utilizatorii înregistrați."
    },
    {
      id: 5,
      question: "Ce fac dacă am probleme tehnice?",
      answer: "Pentru probleme tehnice, te rugăm să ne contactezi prin pagina de contact, specificând detalii despre problemă și browser-ul folosit. Echipa noastră de suport va răspunde în 24 de ore."
    },
    {
      id: 6,
      question: "Cum pot face adnotări în cărți?",
      answer: "Sistemul nostru permite adăugarea de evidențieri, note și marcaje. Folosește toolbar-ul din reader pentru a accesa aceste funcționalități. Toate adnotările tale sunt salvate automat în contul tău."
    }
  ];

  const guides = [
    {
      id: 1,
      title: "Ghid pentru începători",
      description: "Învață să navighezi prin bibliotecă și să folosești funcționalitățile de bază",
      steps: [
        "Creează un cont nou sau autentifică-te",
        "Explorează categoriile de cărți disponibile", 
        "Folosește bara de căutare pentru a găsi cărți specifice",
        "Deschide o carte și familiarizează-te cu reader-ul",
        "Încearcă să faci evidențieri și note"
      ]
    },
    {
      id: 2,
      title: "Căutare avansată",
      description: "Folosește filtrele avansate pentru a găsi exact ce cauți",
      steps: [
        "Accesează opțiunea 'Căutare avansată' din bara de căutare",
        "Filtrează după autor, anul publicării, limbă sau gen",
        "Combină mai multe filtre pentru rezultate mai precise",
        "Salvează căutările frecvente pentru acces rapid",
        "Folosește operatorii booleeni pentru căutări complexe"
      ]
    },
    {
      id: 3,
      title: "Managementul bibliotecii personale",
      description: "Organizează-ți cărțile preferate și istoricul de lectură",
      steps: [
        "Adaugă cărți la favorite folosind iconița de inimă",
        "Creează liste personalizate pentru organizare",
        "Urmărește progresul de lectură în dashboard-ul personal",
        "Exportă listele tale pentru backup",
        "Sincronizează între dispozitive"
      ]
    }
  ];

  const toggleFaq = (id: number) => {
    setOpenFaq(openFaq === id ? null : id);
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Header />
      
      {/* Spațiu pentru header-ul fix */}
      <div className="pt-24 md:pt-28"></div>
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-16">
          <div className="container mx-auto px-4">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">Centrul de Ajutor</h1>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                Găsește răspunsuri la întrebările tale și învață să folosești biblioteca digitală la maximum
              </p>
            </div>
          </div>
        </section>

        {/* Căutare rapidă în ajutor */}
        <section className="py-8 -mt-8">
          <div className="container mx-auto px-4">
            <div className="bg-white rounded-xl shadow-lg p-6 max-w-2xl mx-auto">
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-400 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Caută în secțiunea de ajutor..."
                  className="flex-1 outline-none text-gray-700 placeholder-gray-400"
                />
                <button className="bg-blue-600 text-white px-4 py-2 rounded-lg ml-3 hover:bg-blue-700 transition">
                  Caută
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Secțiuni rapide */}
        <section className="py-8">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <a href="#ghiduri" className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition group">
                <div className="text-blue-600 text-4xl mb-4 group-hover:scale-110 transition-transform">📖</div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Ghiduri de utilizare</h3>
                <p className="text-gray-600">Învață să folosești toate funcționalitățile platformei</p>
              </a>
              
              <a href="#faq" className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition group">
                <div className="text-blue-600 text-4xl mb-4 group-hover:scale-110 transition-transform">❓</div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Întrebări frecvente</h3>
                <p className="text-gray-600">Răspunsuri la cele mai comune întrebări</p>
              </a>
              
              <Link to="/contact" className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition group">
                <div className="text-blue-600 text-4xl mb-4 group-hover:scale-110 transition-transform">💬</div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Contactează suportul</h3>
                <p className="text-gray-600">Nu găsești răspunsul? Scrie-ne direct</p>
              </Link>
            </div>
          </div>
        </section>

        {/* Ghiduri de utilizare */}
        <section id="ghiduri" className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">Ghiduri de Utilizare</h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {guides.map(guide => (
                <div key={guide.id} className="bg-gray-50 p-6 rounded-xl">
                  <h3 className="text-xl font-bold text-gray-800 mb-4">{guide.title}</h3>
                  <p className="text-gray-600 mb-6">{guide.description}</p>
                  
                  <div className="space-y-3">
                    {guide.steps.map((step, index) => (
                      <div key={index} className="flex items-start">
                        <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white text-sm rounded-full flex items-center justify-center mr-3 mt-0.5">
                          {index + 1}
                        </span>
                        <span className="text-gray-700">{step}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section id="faq" className="py-16 bg-gray-100">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">Întrebări Frecvente</h2>
            
            <div className="max-w-3xl mx-auto space-y-4">
              {faqs.map(faq => (
                <div key={faq.id} className="bg-white rounded-xl shadow-md overflow-hidden">
                  <button
                    onClick={() => toggleFaq(faq.id)}
                    className="w-full p-6 text-left flex items-center justify-between hover:bg-gray-50 transition"
                  >
                    <h3 className="text-lg font-medium text-gray-800 pr-4">{faq.question}</h3>
                    <svg
                      className={`w-6 h-6 text-gray-400 transform transition-transform ${
                        openFaq === faq.id ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {openFaq === faq.id && (
                    <div className="px-6 pb-6">
                      <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Troubleshooting rapid */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">Rezolvarea Problemelor</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <div className="bg-red-50 border border-red-200 p-6 rounded-xl">
                <h3 className="text-lg font-bold text-red-800 mb-4 flex items-center">
                  <svg className="w-6 h-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  Cărțile nu se încarcă
                </h3>
                <ul className="space-y-2 text-red-700 text-sm">
                  <li>• Verifică conexiunea la internet</li>
                  <li>• Reîmprospătează pagina (F5)</li>
                  <li>• Încearcă să te deconectezi și să te conectezi din nou</li>
                  <li>• Contactează suportul dacă problema persistă</li>
                </ul>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 p-6 rounded-xl">
                <h3 className="text-lg font-bold text-yellow-800 mb-4 flex items-center">
                  <svg className="w-6 h-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Nu îmi pot aminti parola
                </h3>
                <ul className="space-y-2 text-yellow-700 text-sm">
                  <li>• Folosește opțiunea "Am uitat parola" de pe pagina de login</li>
                  <li>• Verifică emailul (inclusiv spam/junk)</li>
                  <li>• Urmează instrucțiunile din email</li>
                  <li>• Contactează suportul dacă nu primești emailul</li>
                </ul>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 p-6 rounded-xl">
                <h3 className="text-lg font-bold text-blue-800 mb-4 flex items-center">
                  <svg className="w-6 h-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  Performanța lentă
                </h3>
                <ul className="space-y-2 text-blue-700 text-sm">
                  <li>• Închide tab-urile nefolosite din browser</li>
                  <li>• Oprește descărcările în curs</li>
                  <li>• Folosește un browser modern (Chrome, Firefox, Safari)</li>
                  <li>• Verifică viteza conexiunii la internet</li>
                </ul>
              </div>
              
              <div className="bg-green-50 border border-green-200 p-6 rounded-xl">
                <h3 className="text-lg font-bold text-green-800 mb-4 flex items-center">
                  <svg className="w-6 h-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                  </svg>
                  Probleme de partajare
                </h3>
                <ul className="space-y-2 text-green-700 text-sm">
                  <li>• Verifică setările de confidențialitate ale browserului</li>
                  <li>• Permite popup-urile pentru site-ul nostru</li>
                  <li>• Încearcă să copiezi linkul manual</li>
                  <li>• Folosește funcția de export pentru partajare offline</li>
                </ul>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default HelpPage; 
